import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { EstimatedPicker, TagPicker } from '@/features/tasks/public';
import type { Tag } from '@/features/tags/public';
import type { ConvertToTaskFormValues } from '../schemas';
import type { NoteTaskProposal } from '../types';
import { dateHint } from '../utils/convertToTaskHints';
import { ConvertToTaskDateField } from './ConvertToTaskDateField';
import { ConvertToTaskField } from './ConvertToTaskField';

const AI_FILLED = 'border-primary';

interface ConvertToTaskScheduleSectionProps {
  control: Control<ConvertToTaskFormValues>;
  isPending: boolean;
  proposal: NoteTaskProposal | null;
  projectId: string;
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
}

/** Dates, estimate, tags and the observations the proposal made about them. */
export function ConvertToTaskScheduleSection({
  control,
  isPending,
  proposal,
  projectId,
  selectedTags,
  onTagsChange,
}: ConvertToTaskScheduleSectionProps) {
  const { t } = useTranslation('notes');
  const aiShort = t('convertToTask.aiShort');

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <ConvertToTaskDateField
          control={control}
          name="startDate"
          label={t('convertToTask.fields.startDate')}
          placeholder={t('convertToTask.fields.datePlaceholder')}
          aiLabel={proposal?.startDateExpression ? aiShort : undefined}
          hint={dateHint(t, proposal?.startDateExpression, proposal?.startDate)}
          disabled={isPending}
          highlighted={!!proposal?.startDate}
          data-testid="notes-convert-startDate-input"
        />
        <ConvertToTaskDateField
          control={control}
          name="dueDate"
          label={t('convertToTask.fields.dueDate')}
          placeholder={t('convertToTask.fields.datePlaceholder')}
          aiLabel={proposal?.dueDateExpression ? aiShort : undefined}
          hint={dateHint(t, proposal?.dueDateExpression, proposal?.dueDate)}
          disabled={isPending}
          highlighted={!!proposal?.dueDate}
          data-testid="notes-convert-dueDate-input"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* The proposal fills this field too — without it the estimate would
            be submitted without the user ever seeing it. */}
        <Controller
          name="estimatedMinutes"
          control={control}
          render={({ field }) => (
            <ConvertToTaskField
              label={t('convertToTask.fields.estimatedTime')}
              aiLabel={proposal?.estimatedMinutes != null ? aiShort : undefined}
            >
              <div
                className={cn(
                  'flex h-10 items-center rounded-md border px-3 shadow-xs',
                  proposal?.estimatedMinutes != null && AI_FILLED,
                  isPending && 'opacity-50',
                )}
              >
                <EstimatedPicker
                  value={field.value ?? null}
                  onSave={(minutes) => field.onChange(minutes)}
                  disabled={isPending}
                />
              </div>
            </ConvertToTaskField>
          )}
        />
      </div>

      {/* Same picker as the task detail — it can create a tag inline, so the
          user is not sent to the project settings mid-conversion. */}
      <Controller
        name="tags"
        control={control}
        render={({ field }) => (
          <ConvertToTaskField
            label={t('convertToTask.fields.tags')}
            hint={!projectId ? t('convertToTask.fields.tagsNeedProject') : undefined}
          >
            <TagPicker
              projectId={projectId}
              selectedTags={selectedTags}
              onChange={(tags) => {
                onTagsChange(tags);
                field.onChange(tags.map((tag) => tag.id).filter((id): id is string => !!id));
              }}
              disabled={isPending || !projectId}
            />
          </ConvertToTaskField>
        )}
      />

      {proposal?.observations && proposal.observations.length > 0 && (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {proposal.observations.map((observation) => (
            <li key={observation} className="flex items-start gap-1.5">
              <Sparkles className="mt-0.5 size-3 shrink-0 text-primary" aria-hidden />
              {observation}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
